import * as THREE from 'three';

// Vite picks up these PNGs and gives us URL strings keyed by path.
const glyphUrls = import.meta.glob('../../assets/td/glyphs/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
});

// "../../assets/td/glyphs/A.png" -> "A.png"
const urlByFilename = {};
for (const [path, url] of Object.entries(glyphUrls)) {
  const fname = path.split('/').pop();
  urlByFilename[fname] = url;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`failed to load ${url}: ${e.message ?? e}`));
    img.src = url;
  });
}

export async function buildTextAtlas(chars, atlasCfg, glyphMap) {
  const unique = Array.from(new Set(chars));
  const cell = atlasCfg.cellSize ?? 256;
  const cols = Math.ceil(Math.sqrt(unique.length));
  const rows = Math.ceil(unique.length / cols);

  const canvas = document.createElement('canvas');
  canvas.width = cols * cell;
  canvas.height = rows * cell;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const uvMap = new Map();

  await Promise.all(unique.map(async (ch, i) => {
    const fname = glyphMap?.[ch];
    const url = fname ? urlByFilename[fname] : undefined;

    const cx = i % cols;
    const cy = Math.floor(i / cols);
    const px = cx * cell;
    const py = cy * cell;

    if (url) {
      try {
        const img = await loadImage(url);
        ctx.drawImage(img, px, py, cell, cell);
        normalizeCellToMask(ctx, px, py, cell);
      } catch (err) {
        console.warn(err);
        drawFallback(ctx, ch, px, py, cell);
        normalizeCellToMask(ctx, px, py, cell);
      }
    } else {
      drawFallback(ctx, ch, px, py, cell);
      normalizeCellToMask(ctx, px, py, cell);
    }

    const u0 = px / canvas.width;
    const v0 = 1 - (py + cell) / canvas.height;
    const u1 = (px + cell) / canvas.width;
    const v1 = 1 - py / canvas.height;
    uvMap.set(ch, { u0, v0, u1, v1 });
  }));

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;

  return { texture, uvMap, canvas };
}

// Reads back the cell, infers background color from the four corners, and
// rewrites the cell as a shader mask. Transparent-background glyphs become
// letter-only masks; filled-background glyphs stay filled, with their inner
// letter knocked out by color/alpha distance from the background.
function normalizeCellToMask(ctx, px, py, cell) {
  const img = ctx.getImageData(px, py, cell, cell);
  const d = img.data;
  const last = cell - 1;
  const corners = [
    [0, 0], [last, 0], [0, last], [last, last],
  ];

  let bgR = 0, bgG = 0, bgB = 0, bgA = 0;
  for (const [cx, cy] of corners) {
    const i = (cy * cell + cx) * 4;
    bgR += d[i]; bgG += d[i + 1]; bgB += d[i + 2]; bgA += d[i + 3];
  }
  bgR /= 4; bgG /= 4; bgB /= 4; bgA /= 4;

  const transparentBg = bgA < 32;

  for (let i = 0; i < d.length; i += 4) {
    let ink;
    if (transparentBg) {
      // Background is transparent — alpha is the ink signal.
      ink = d[i + 3];
    } else {
      // Filled tile — keep the cell filled and knock out the inner letter.
      const dr = Math.abs(d[i + 0] - bgR);
      const dg = Math.abs(d[i + 1] - bgG);
      const db = Math.abs(d[i + 2] - bgB);
      const da = Math.abs(d[i + 3] - bgA);
      ink = 255 - Math.min(255, Math.max(dr, dg, db, da) * 1.6);
    }
    d[i + 0] = ink;
    d[i + 1] = ink;
    d[i + 2] = ink;
    d[i + 3] = ink;
  }
  ctx.putImageData(img, px, py);
}

function drawFallback(ctx, ch, px, py, cell) {
  ctx.save();
  ctx.fillStyle = '#000';
  ctx.fillRect(px, py, cell, cell);
  ctx.fillStyle = '#fff';
  ctx.font = `700 ${Math.floor(cell * 0.7)}px "JetBrains Mono", monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(ch, px + cell / 2, py + cell / 2);
  ctx.restore();
}

export function uvForChar(uvMap, ch, fallback = ' ') {
  return uvMap.get(ch) ?? uvMap.get(fallback) ?? { u0: 0, v0: 0, u1: 1, v1: 1 };
}
